import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LightSurface } from '../theme/ThemeProvider';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FileDown, Lock, Printer, ReceiptText } from 'lucide-react';
import { Button, EmptyState, Modal, Skeleton, StatusPill, TextInput } from '../ui';
import {
    buildPlayerInvoice,
    buildSpendInvoice,
    buildTableInvoice,
    DEFAULT_INVOICE_TEMPLATE,
    downloadInvoicePDF,
    formatAmount,
    gstRowsOf,
    printInvoicePDF,
} from '../utils/invoiceUtils';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

export const fetchInvoiceTemplate = async (token) => {
    const { data } = await axios.get(`${API_URL}/api/invoice-template`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data.data;
};

export const saveInvoiceTemplate = async (token, template) => {
    const { data } = await axios.put(`${API_URL}/api/invoice-template`, template, {
        headers: { Authorization: `Bearer ${token}` },
    });
    return data.data;
};

const rupees = (amount) => `₹${formatAmount(amount)}`;

const TotalRow = ({ label, value, strong }) => (
    <div
        className={`flex justify-between gap-4 px-3 py-1.5 text-sm ${strong ? 'rounded-cm bg-cm-surface-3 font-bold text-cm-text' : 'text-cm-text'}`}
    >
        <span>{label}</span>
        <span className={`tabular-nums ${strong ? '' : 'font-medium'}`}>{value}</span>
    </div>
);

// On-screen rendering of the bill; mirrors the PDF layout. Always drawn light, like paper.
export const InvoicePreview = ({ invoice }) => {
    const tpl = invoice.template;
    const business = invoice.business;
    return (
        <LightSurface>
            <div className="rounded-cm-lg border border-cm-border bg-white p-4 md:p-6 text-cm-text shadow-cm-sm">
                <div className="flex flex-wrap justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-lg font-bold m-0">{business.name || 'Business name'}</p>
                        {business.address && (
                            <p className="text-xs text-cm-muted whitespace-pre-line m-0">{business.address}</p>
                        )}
                        {invoice.gstin && <p className="text-xs text-cm-muted m-0">GSTIN: {invoice.gstin}</p>}
                        {(business.phone || business.email) && (
                            <p className="text-xs text-cm-muted m-0">
                                {[business.phone, business.email].filter(Boolean).join(' | ')}
                            </p>
                        )}
                    </div>
                    <div className="text-right">
                        <p className="text-lg font-bold m-0">{tpl.invoiceTitle}</p>
                        <p className="text-xs text-cm-muted m-0">Invoice No: {invoice.invoiceNumber}</p>
                        <p className="text-xs text-cm-muted m-0">
                            Date: {moment(invoice.invoiceDate).format('DD MMM YYYY, hh:mm A')}
                        </p>
                        {invoice.isPrivate && (
                            <StatusPill tone="warning" icon={Lock} size="sm" className="mt-1.5">
                                Private table booking
                            </StatusPill>
                        )}
                    </div>
                </div>

                <hr className="border-cm-border my-4" />

                <div className="flex flex-wrap gap-6 mb-4">
                    <div className="flex-1 min-w-[160px]">
                        <p className="text-sm font-bold m-0">Bill To</p>
                        <p className="text-sm whitespace-pre-line m-0">{invoice.billTo || '-'}</p>
                    </div>
                    <div className="flex-1 min-w-[160px]">
                        <p className="text-sm font-bold m-0">{invoice.details.title}</p>
                        {invoice.details.lines.map((line, idx) => (
                            <p key={line} className={`m-0 ${idx === 0 ? 'text-sm' : 'text-xs text-cm-muted'}`}>
                                {line}
                            </p>
                        ))}
                    </div>
                </div>

                {invoice.isPrivate && (
                    <div className="mb-4 rounded-cm bg-cm-warning-soft px-3 py-2.5">
                        <p className="text-sm font-bold text-cm-warning m-0">Private Table Booking</p>
                        {invoice.privateBookings.map((booking) => (
                            <p key={booking._id} className="text-xs m-0">
                                {booking.period} · {booking.hours} hr · Booking price {rupees(booking.price)}
                            </p>
                        ))}
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-cm-border-strong text-left text-xs font-semibold text-cm-muted">
                                <th className="py-2 pr-3">#</th>
                                <th className="py-2 pr-3">Description</th>
                                {tpl.sacCode && <th className="py-2 pr-3">SAC</th>}
                                {!invoice.hideUsage && (
                                    <th className="py-2 pr-3 text-right">Usage ({tpl.unitLabel})</th>
                                )}
                                <th className="py-2 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoice.lines.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-4 text-center text-cm-muted">
                                        {invoice.emptyMessage}
                                    </td>
                                </tr>
                            )}
                            {invoice.lines.map((line, idx) => (
                                <tr key={`${line.period}-${idx}`} className="border-b border-cm-border">
                                    <td className="py-2 pr-3 align-top tabular-nums">{idx + 1}</td>
                                    <td className="py-2 pr-3 align-top">
                                        <div>{line.description}</div>
                                        <div className="text-xs text-cm-muted">{line.period}</div>
                                    </td>
                                    {tpl.sacCode && <td className="py-2 pr-3 align-top">{tpl.sacCode}</td>}
                                    {!invoice.hideUsage && (
                                        <td className="py-2 pr-3 align-top text-right tabular-nums">{line.hours}</td>
                                    )}
                                    <td className="py-2 align-top text-right tabular-nums">{rupees(line.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="ml-auto mt-4 w-full sm:w-80">
                    {!invoice.hideUsage && (
                        <TotalRow label={`Unit Usage (${tpl.unitLabel})`} value={invoice.unitUsage} />
                    )}
                    <TotalRow
                        label={invoice.gstInclusive ? 'Taxable value' : tpl.chargesLabel}
                        value={rupees(invoice.charges)}
                    />
                    {gstRowsOf(invoice).map((row) => (
                        <TotalRow key={row.label} label={row.label} value={rupees(row.amount)} />
                    ))}
                    <TotalRow label={tpl.totalLabel} value={rupees(invoice.total)} strong />
                </div>

                {invoice.inProgress && (
                    <p className="text-xs font-semibold text-cm-warning mt-4 mb-0">{invoice.inProgressNote}</p>
                )}
                {invoice.gstInclusive && (
                    <p className="text-xs text-cm-muted mt-4 mb-0">
                        * Amounts are inclusive of GST and were paid from the player wallet.
                    </p>
                )}
                {tpl.notes && <p className="text-xs text-cm-muted whitespace-pre-line mt-4 mb-0">{tpl.notes}</p>}
                {tpl.footerText && <p className="text-sm text-cm-muted text-center mt-6 mb-0">{tpl.footerText}</p>}
            </div>
        </LightSurface>
    );
};

// Loads the invoice template while open and shows the preview with print/download actions.
// `buildInvoice(template, billTo, generatedAt)` returns the invoice to render.
const InvoiceDialog = ({ open, onClose, token, blockedMessage, withBillTo, buildInvoice }) => {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [billTo, setBillTo] = useState('');

    useEffect(() => {
        if (!open || !token || blockedMessage) return;
        setBillTo('');
        setLoading(true);
        fetchInvoiceTemplate(token)
            .then(setTemplate)
            .catch(() => {
                toast.warning('Could not load invoice template; using defaults');
                setTemplate(DEFAULT_INVOICE_TEMPLATE);
            })
            .finally(() => setLoading(false));
    }, [open, token, blockedMessage]);

    // Freeze the invoice date when the dialog opens so preview and PDF agree
    const generatedAt = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const invoice = useMemo(
        () => (template && !blockedMessage ? buildInvoice(template, billTo, generatedAt) : null),
        [template, billTo, generatedAt, blockedMessage, buildInvoice]
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Generate invoice"
            description={blockedMessage ? undefined : 'Check the bill, then print it or download the PDF.'}
            icon={ReceiptText}
            size="xl"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                    <Button variant="secondary" icon={Printer} disabled={!invoice} onClick={() => printInvoicePDF(invoice)}>
                        Print
                    </Button>
                    <Button icon={FileDown} disabled={!invoice} onClick={() => downloadInvoicePDF(invoice)}>
                        Download PDF
                    </Button>
                </>
            }
        >
            {blockedMessage ? (
                <EmptyState compact icon={Lock} title="Not ready to invoice" description={blockedMessage} />
            ) : loading || !invoice ? (
                <div className="flex flex-col gap-3">
                    <Skeleton className="h-10" />
                    <Skeleton className="h-72 rounded-cm-lg" />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {withBillTo && (
                        <TextInput
                            label="Bill to"
                            help="Optional. Printed on the invoice."
                            placeholder="Customer / player name"
                            value={billTo}
                            onChange={(event) => setBillTo(event.target.value)}
                        />
                    )}
                    <InvoicePreview invoice={invoice} />
                </div>
            )}
        </Modal>
    );
};

/**
 * Generates a bill for a table from the Player & Table Report.
 * `runs` defaults to every run of the table in the report period.
 */
export const TableInvoiceDialog = ({ open, onClose, table, runs, token }) => {
    const invoiceRuns = useMemo(() => runs || table?.runs || [], [runs, table]);
    const hasRunningRun = invoiceRuns.some((run) => !run.endedAt);
    const buildInvoice = useCallback(
        (template, billTo, generatedAt) =>
            table ? buildTableInvoice({ table, runs: invoiceRuns, template, billTo, generatedAt }) : null,
        [table, invoiceRuns]
    );

    return (
        <InvoiceDialog
            open={open}
            onClose={onClose}
            token={token}
            withBillTo
            blockedMessage={
                hasRunningRun ? 'Invoice can be generated only after the table has stopped.' : null
            }
            buildInvoice={buildInvoice}
        />
    );
};

/**
 * Generates a bill for a player from the Player & Table Report.
 * `sessions` defaults to every session of the player in the (table-filtered) period.
 */
export const PlayerInvoiceDialog = ({ open, onClose, player, sessions, tables, token }) => {
    const invoiceSessions = useMemo(() => sessions || player?.sessions || [], [sessions, player]);
    const isSeated =
        Boolean(player?.isSeated) ||
        (player?.sessions || []).some((session) => !session.endedAt) ||
        invoiceSessions.some((session) => !session.endedAt);
    const buildInvoice = useCallback(
        (template, _billTo, generatedAt) =>
            player
                ? buildPlayerInvoice({ player, sessions: invoiceSessions, tables, template, generatedAt })
                : null,
        [player, invoiceSessions, tables]
    );

    return (
        <InvoiceDialog
            open={open}
            onClose={onClose}
            token={token}
            blockedMessage={
                isSeated ? 'Player is seated at a table. Invoice can be generated only after the player leaves.' : null
            }
            buildInvoice={buildInvoice}
        />
    );
};

/**
 * Bill for one Add Spend from the Transactions > Spend tab.
 * `transactions` are the spend's wallet rows (one per item); `labelOf(txn)` names the item.
 */
export const SpendInvoiceDialog = ({ open, onClose, transactions, labelOf, token }) => {
    const buildInvoice = useCallback(
        (template, _billTo, generatedAt) =>
            transactions?.length ? buildSpendInvoice({ transactions, labelOf, template, generatedAt }) : null,
        [transactions, labelOf]
    );

    return <InvoiceDialog open={open} onClose={onClose} token={token} buildInvoice={buildInvoice} />;
};
